import React, { memo, useMemo, useState } from 'react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@common/components/ui/accordion';
import { Icon } from '@common/components/icon';
import { APIEmployeeAcademyCourseSection } from '@modules/learning/types/objects';
import ModuleRow from './module-row';

import './course-section.scss';

type CourseSectionProps = {
  section: APIEmployeeAcademyCourseSection;
};

const CourseSection = memo(({ section }: CourseSectionProps) => {

  const content = useMemo(() => {
    return section.modules.map((module) => {
      return <ModuleRow key={module.id} module={module} />;
    });
  }, [section]);

  const finishedModulesCount = useMemo(() => {
    return section.modules.reduce((acc, module) => {
      if (module.progress.finished) {
        return acc + 1;
      }
      return acc;
    }, 0);
  }, [section]);

  if (section.transparent) {
    return <>{ content }</>;
  }

  const sectionFinished = finishedModulesCount >= section.modules.length;
  const [openItems, setOpenItems] = useState<string[]>(!sectionFinished ? [`section-${section.id}`] : []);

  return (
    <Accordion
      type="multiple"
      value={openItems}
      onValueChange={setOpenItems}
      className="CourseSection"
    >
      <AccordionItem
        value={`section-${section.id}`}
        className="SimpleAccordionItem tw-mb-0 tw-border tw-border-solid last:!tw-border-b tw-rounded-lg tw-border-gray-border"
      >
        <AccordionTrigger
          icon={<Icon type="expand_more" className="tw-mx-2" size={34} />}
          className="tw-rounded-lg [&[data-state=open]]:tw-rounded-b-none tw-py-3 tw-px-4 tw-border-none"
        >
          <div className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-w-full">
            <h3>{section.name || ''}</h3>
            <div className="SimpleAccordionItem__RightSide">
              {sectionFinished ? (
                <Icon className="CourseSection__Check [&_path]:tw-stroke-green-custom [&_path]:tw-stroke-[3px]" type="check" />
              ) : (
                <small>
                  {finishedModulesCount}/{section.modules.length}
                </small>
              )}
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="tw-pt-0">
          <div className="[&_.AcademyModuleIcon]:tw-ml-5">
            { content }
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
});

export default CourseSection;
